import React, { useState, useEffect } from 'react';
import { StyledModal } from './SiteSurveyOverlay.styles';
import { SiteSurveyHeader } from './SiteSurveyHeader';
import { theme, message, Spin, Form } from 'antd';
import { useStores } from '@/hooks/use-stores';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import UnifiedSiteSurveyForm from '@/comp/Forms/SiteSurveyForm/UnifiedSiteSurveyForm';
import { observer } from 'mobx-react-lite';
import { CustomerInformationStepData, SiteConfigurationStepData } from '@/store/SiteSurveyForm/constants';
import {
  fetchAccountDataForSurvey,
  SiteSurveyOverlayProps,
  FormData,
  isSiteAddressEmpty,
} from './SiteSurveyOverlay.utils';
import { RoomConfigurationStepValues } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/RoomConfigurationContent.utils';
import { getTierAndUsageFromSelection } from '@/comp/Forms/SiteSurveyForm/CustomerInformationStep/CustomerInformationContent.utils';
import { ResponsiveProvider } from '@/hooks/useResponsive';
import { AddressWarningModal } from './AddressWarningModal';

const { useToken } = theme;

const SiteSurveyOverlay: React.FC<SiteSurveyOverlayProps> = observer(({ visible, onClose, salesforceId }) => {
  const [form] = Form.useForm();

  const [formData, setFormData] = useState<FormData>(() => ({
    ...(salesforceId ? { salesforceId, accountId: salesforceId } : {}),
  }));

  const [isLoading, setIsLoading] = useState(!!salesforceId);
  const [isSaving, setIsSaving] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(!salesforceId);
  const [isThankYouScreenShown, setIsThankYouScreenShown] = useState(false);
  const [isAddressWarningModalVisible, setIsAddressWarningModalVisible] = useState(false);
  const [pendingCustomerInformationValues, setPendingCustomerInformationValues] =
    useState<CustomerInformationStepData>(null);
  const totalSteps = 3;
  const { token } = useToken();
  const navigate = useNavigate();

  const { siteSurveyStore, userStore } = useStores();

  useEffect(() => {
    if (siteSurveyStore?.isUserHasInfoCollectionPermission !== true) {
      onClose();
    }
  }, [siteSurveyStore.isUserHasInfoCollectionPermission]);

  useEffect(() => {
    if (visible && isThankYouScreenShown) {
      const stationId = siteSurveyStore.getStationId();

      if (stationId) {
        setTimeout(async () => {
          await userStore.getMe();
          navigate(`/home/station/${stationId}/tab/map`);
          handleClose();
        }, 100);
      }
    }
  }, [siteSurveyStore.stationId, isThankYouScreenShown, visible]);

  useEffect(() => {
    if (visible && salesforceId && !initialLoadComplete) {
      setIsLoading(true);
      siteSurveyStore.resetForm();

      (async () => {
        try {
          const updatedFormData = await fetchAccountDataForSurvey(
            salesforceId,
            siteSurveyStore.fetchAccountData.bind(siteSurveyStore),
            formData,
          );
          setFormData(updatedFormData);
          const existingForms = await siteSurveyStore.fetchManyFormBySalesforceAccountForContinue(salesforceId);
          if (existingForms && existingForms.length === 0) {
            siteSurveyStore.setCurrentStep(1);
          } else {
            siteSurveyStore.setCurrentStep(0);
          }

          form.setFieldValue('salesforceAccountId', salesforceId);
          form.setFieldValue('accountId', salesforceId);
          form.setFieldValue('region', siteSurveyStore.selectedRegion);
        } catch (error) {
          siteSurveyStore.setCurrentStep(0);
        } finally {
          setIsLoading(false);
          setInitialLoadComplete(true);
        }
      })();
    }
  }, [visible, salesforceId, initialLoadComplete, formData, siteSurveyStore, form]);

  const handleClose = (): void => {
    siteSurveyStore.setCurrentStep(salesforceId ? 1 : 0);
    setFormData({
      ...(salesforceId ? { salesforceId, accountId: salesforceId } : {}),
    });
    setInitialLoadComplete(!salesforceId);
    setIsThankYouScreenShown(false);
    siteSurveyStore.resetForm();
    form.resetFields();
    onClose();
  };

  const handleHomeSubmit = async (values: { accountId: string }): Promise<void> => {
    try {
      setIsLoading(true);
      form.setFieldValue('salesforceAccountId', values.accountId);

      const updatedFormData = await fetchAccountDataForSurvey(
        values.accountId,
        siteSurveyStore.fetchAccountData.bind(siteSurveyStore),
        formData,
      );
      form.setFieldValue('accountId', updatedFormData.accountId);
      setFormData(updatedFormData);

      const existingForms = await siteSurveyStore.fetchManyFormBySalesforceAccountForContinue(
        updatedFormData.accountId,
      );
      if (existingForms && existingForms.length === 0) {
        siteSurveyStore.setCurrentStep(1);
      }
    } catch (error) {
      // Error already handled in the utility function
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndExit = async (): Promise<void> => {
    setIsSaving(true);

    try {
      await form.validateFields();

      const currentStep = siteSurveyStore.currentStep;
      const formId = formData.formId || siteSurveyStore.getFormId();
      const allValues = form.getFieldsValue(true);
      const salesforceAccountId = form.getFieldValue('salesforceAccountId');

      if (currentStep === 1 && !salesforceAccountId) {
        throw new Error('Salesforce Account ID is required to save form data');
      }

      if ((currentStep === 2 || currentStep === 3) && !formId) {
        throw new Error('Form ID is missing, cannot save data.');
      }

      siteSurveyStore.updateCustomerInformationData(allValues as CustomerInformationStepData);
      siteSurveyStore.updateSiteConfigurationData(allValues as SiteConfigurationStepData);
      siteSurveyStore.updateRoomConfigurationStepData(allValues as RoomConfigurationStepValues);

      if (currentStep === 1) {
        const result = await siteSurveyStore.saveCustomerInformationStepData(salesforceAccountId);

        if (result?.formId) {
          siteSurveyStore.setFormId(result.formId);
          setFormData(prev => ({ ...prev, formId: result.formId }));
        } else if (!formId) {
          console.warn('Form ID was not established after saving Step 1.');
        }
      }

      await siteSurveyStore.saveSiteConfigurationStepData();
      await siteSurveyStore.saveRoomConfigurationStepData();

      message.success(t('Progress saved successfully.'));
      handleClose();
    } catch (error) {
      console.error('Error saving form data:', error);
      message.error(t('Failed to save progress. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomerInformationStepNext = async (values: CustomerInformationStepData): Promise<void> => {
    const siteAddress = values.siteAddress;

    if (isSiteAddressEmpty(siteAddress)) {
      setPendingCustomerInformationValues(values);
      setIsAddressWarningModalVisible(true);
      return;
    }

    await proceedWithCustomerInformationStep(values);
  };

  const proceedWithCustomerInformationStep = async (values: CustomerInformationStepData): Promise<void> => {
    try {
      setIsLoading(true);
      siteSurveyStore.updateCustomerInformationData(values);

      const salesforceAccountId = form.getFieldValue('salesforceAccountId');

      if (!salesforceAccountId) {
        throw new Error('Salesforce Account ID is required to save form data');
      }

      let formId = formData.formId || siteSurveyStore.getFormId();

      const result = await siteSurveyStore.saveCustomerInformationStepData(salesforceAccountId);
      if (result?.formId) {
        formId = result.formId;
        siteSurveyStore.setFormId(formId);
        setFormData(prev => ({
          ...prev,
          formId,
        }));
      }

      siteSurveyStore.setCurrentStep(2);
    } catch (error) {
      if (error instanceof Error && error.message.includes('Salesforce Account ID')) {
        message.error(t('Cannot proceed without Salesforce Account ID. Please start again.'));
      } else {
        message.error(t('Failed to save customer details. The form will continue, but data may not be saved.'));
        siteSurveyStore.setCurrentStep(2);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseAddressWarningModal = (): void => {
    setIsAddressWarningModalVisible(false);
    setPendingCustomerInformationValues(null);
  };

  const handleConfirmAddressWarningModal = async (): Promise<void> => {
    setIsAddressWarningModalVisible(false);

    if (pendingCustomerInformationValues) {
      await proceedWithCustomerInformationStep(pendingCustomerInformationValues);
    }

    setPendingCustomerInformationValues(null);
  };

  const handleSiteConfigurationStepNext = async (
    values: { customerInformationStepData?: CustomerInformationStepData } & SiteConfigurationStepData,
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const { customerInformationStepData, ...siteConfigurationStepValues } = values;
      siteSurveyStore.updateSiteConfigurationData(siteConfigurationStepValues);

      if (customerInformationStepData) {
        siteSurveyStore.updateCustomerInformationData(customerInformationStepData);
      }

      const formId = formData.formId || siteSurveyStore.getFormId();

      if (formId) {
        siteSurveyStore.setFormId(formId);
        setFormData(prev => ({
          ...prev,
          formId,
        }));
      } else {
        message.warning(t('Site configuration data may not be properly saved'));
      }

      await siteSurveyStore.saveSiteConfigurationStepData();

      try {
        await siteSurveyStore.fetchModelSpecs();
      } catch (error) {
        message.error(t('Failed to fetch model specifications. Some dropdown options may not be available.'));
      }

      await siteSurveyStore.fetchManufacturersList();

      siteSurveyStore.setCurrentStep(3);
    } catch (error) {
      message.error(t('Failed to save site configuration. The form will continue, but data may not be saved.'));
      siteSurveyStore.setCurrentStep(3);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoomConfigurationStepFinish = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const allValues = form.getFieldsValue(true);
      const updatedValues = { ...allValues, showThankYouScreen: true };
      const customerInformationData: Partial<CustomerInformationStepData> = {};
      if (updatedValues.tier) {
        customerInformationData.tier = getTierAndUsageFromSelection(updatedValues.tier).tier;
      }
      if (updatedValues.account) {
        customerInformationData.account = updatedValues.account;
      }
      if (Object.keys(updatedValues).length > 0) {
        siteSurveyStore.updateCustomerInformationData(customerInformationData);
      }
      siteSurveyStore.updateRoomConfigurationStepData(updatedValues as RoomConfigurationStepValues);

      await siteSurveyStore.saveRoomConfigurationStepData();
      await siteSurveyStore.submitOnboardingForm();

      setIsThankYouScreenShown(true);
    } catch (error) {
      console.error('Error in step 3 finish:', error);
      message.error(t('Failed to save and submit assets assignment data. Please try again.'));
      setIsThankYouScreenShown(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = (): void => {
    if (siteSurveyStore.currentStep > 0 && !isThankYouScreenShown) {
      if (siteSurveyStore.currentStep === 2) {
        const formId = formData.formId || siteSurveyStore.getFormId();
        if (formId) {
          siteSurveyStore.setFormId(formId);
          setFormData(prevData => ({
            ...prevData,
            formId,
          }));
        }
      }

      siteSurveyStore.setCurrentStep(siteSurveyStore.currentStep - 1);
    }
  };

  const headerStep = isThankYouScreenShown ? totalSteps + 1 : siteSurveyStore.currentStep;

  if (isLoading && !initialLoadComplete) {
    return (
      <ResponsiveProvider>
        <StyledModal
          open={visible}
          onCancel={handleClose}
          footer={null}
          width="100vw"
          destroyOnClose
          maskClosable={false}
          centered={false}
          closable={false}
          className="site-survey-modal-custom"
          theme={{ token }}
        >
          <div className="content-container">
            <SiteSurveyHeader currentStep={headerStep} totalSteps={totalSteps} onClose={handleClose} />
            <div className="form-container-wrapper">
              <div className="form-container">
                <div className="spinner-container">
                  <Spin size="large" />
                </div>
              </div>
            </div>
          </div>
        </StyledModal>
      </ResponsiveProvider>
    );
  }
  if (siteSurveyStore?.isUserHasInfoCollectionPermission) {
    return (
      <ResponsiveProvider>
        <StyledModal
          open={visible}
          onCancel={handleClose}
          footer={null}
          width="100vw"
          destroyOnClose
          maskClosable={false}
          centered={false}
          closable={false}
          className="site-survey-modal-custom"
          theme={{ token }}
        >
          <div className="content-container">
            <SiteSurveyHeader
              currentStep={headerStep}
              totalSteps={totalSteps}
              onBack={siteSurveyStore.currentStep > 0 && !isThankYouScreenShown ? handleBack : undefined}
              onClose={handleClose}
              onSaveAndExit={handleSaveAndExit}
            />
            <div className="form-container-wrapper">
              <div className="form-container">
                <UnifiedSiteSurveyForm
                  form={form}
                  isLoading={isLoading || isSaving}
                  isThankYouScreenShown={isThankYouScreenShown}
                  initialAccountId={salesforceId || undefined}
                  onHomeSubmit={handleHomeSubmit}
                  onCustomerInformationStepNext={handleCustomerInformationStepNext}
                  onSiteConfigurationStepNext={handleSiteConfigurationStepNext}
                  onRoomConfigurationStepFinish={handleRoomConfigurationStepFinish}
                  onPrevious={handleBack}
                  onClose={handleClose}
                  formData={formData}
                />
              </div>
            </div>
          </div>
        </StyledModal>
        <AddressWarningModal
          visible={isAddressWarningModalVisible}
          onProceed={handleConfirmAddressWarningModal}
          onCancel={handleCloseAddressWarningModal}
        />
      </ResponsiveProvider>
    );
  }
});

export default SiteSurveyOverlay;
